"use strict";
/**
 * Central secret/PII redaction used by the logger before serialisation. Enforcing
 * redaction here (rather than per call site) guarantees coverage regardless of
 * what callers pass. The pass is deep, cycle-safe, depth-bounded and size-capped.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SENSITIVE_EXACT_KEYS = exports.SENSITIVE_KEY_PATTERNS = exports.TRUNCATED = exports.REDACTED = void 0;
exports.redact = redact;
exports.REDACTED = "[REDACTED]";
exports.TRUNCATED = "…[truncated]";
/** Lower-cased substrings; a key is redacted if it contains any of these. */
exports.SENSITIVE_KEY_PATTERNS = [
    "authorization",
    "password",
    "passwd",
    "secret",
    "token",
    "apikey",
    "api_key",
    "cookie",
    "sessionid",
    "session_id",
    "privatekey",
    "private_key",
    "mnemonic",
    "cvv",
    "cardnumber",
    "card_number",
    "creditcard",
    "bsb",
    "accountnumber",
    "account_number",
];
/**
 * Lower-cased whole-key matches. These are too short/generic for substring
 * matching (e.g. "pan" would otherwise redact "companyId", "japan", "plan"), so
 * a key is only redacted when it equals one of these exactly.
 */
exports.SENSITIVE_EXACT_KEYS = ["pan"];
/** Value heuristics for tokens that may appear without a sensitive key. */
const BEARER_RE = /^Bearer\s+/i;
const JWT_RE = /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/;
const DEFAULTS = {
    maxStringLength: 2000,
    maxDepth: 8,
    maxArrayLength: 100,
};
function isSensitiveKey(key) {
    const lowered = key.toLowerCase();
    if (exports.SENSITIVE_EXACT_KEYS.includes(lowered)) {
        return true;
    }
    return exports.SENSITIVE_KEY_PATTERNS.some((pattern) => lowered.includes(pattern));
}
function redactString(value, maxStringLength) {
    if (BEARER_RE.test(value) || JWT_RE.test(value)) {
        return exports.REDACTED;
    }
    if (value.length > maxStringLength) {
        return `${value.slice(0, maxStringLength)}${exports.TRUNCATED}`;
    }
    return value;
}
/**
 * Returns a redacted, serialisation-safe copy of `input`. Sensitive keys and
 * token-shaped values become `[REDACTED]`; oversized strings/arrays and overly
 * deep structures are truncated with a marker rather than dropped; cycles are
 * replaced with `[Circular]`.
 */
function redact(input, options) {
    const opts = { ...DEFAULTS, ...options };
    const seen = new WeakSet();
    const walk = (value, depth) => {
        if (value === null || value === undefined) {
            return value;
        }
        const valueType = typeof value;
        if (valueType === "string") {
            return redactString(value, opts.maxStringLength);
        }
        if (valueType === "number" || valueType === "boolean" || valueType === "bigint") {
            return value;
        }
        if (valueType === "function" || valueType === "symbol") {
            return undefined;
        }
        if (value instanceof Error) {
            const serialised = {
                name: value.name,
                message: redactString(value.message, opts.maxStringLength),
            };
            // Preserve the stack (redacted + truncated) so production error logs are
            // diagnosable when an Error is passed through structured meta rather than
            // formatted into the message. Without this, callers that log
            // `logger.error("...", { err })` lose the stack silently.
            if (typeof value.stack === "string" && value.stack.length > 0) {
                serialised.stack = redactString(value.stack, opts.maxStringLength);
            }
            // Flatten the cause chain (bounded, non-recursive) so wrapped errors
            // still carry the underlying failure into logs.
            const causeChain = [];
            let current = value.cause;
            let causeDepth = 0;
            while (current && causeDepth < 5) {
                if (current instanceof Error) {
                    causeChain.push({
                        name: current.name || "Error",
                        message: redactString(current.message, opts.maxStringLength),
                    });
                    current = current.cause;
                }
                else {
                    causeChain.push({ name: "Cause", message: redactString(String(current), opts.maxStringLength) });
                    current = undefined;
                }
                causeDepth += 1;
            }
            if (causeChain.length > 0) {
                serialised.cause = causeChain;
            }
            return serialised;
        }
        if (depth >= opts.maxDepth) {
            return exports.TRUNCATED;
        }
        if (Array.isArray(value)) {
            if (seen.has(value)) {
                return "[Circular]";
            }
            seen.add(value);
            const result = value.slice(0, opts.maxArrayLength).map((entry) => walk(entry, depth + 1));
            if (value.length > opts.maxArrayLength) {
                result.push(exports.TRUNCATED);
            }
            return result;
        }
        if (valueType === "object") {
            const objectValue = value;
            if (seen.has(objectValue)) {
                return "[Circular]";
            }
            seen.add(objectValue);
            const result = {};
            Object.entries(objectValue).forEach(([key, nested]) => {
                result[key] = isSensitiveKey(key) ? exports.REDACTED : walk(nested, depth + 1);
            });
            return result;
        }
        return value;
    };
    return walk(input, 0);
}
//# sourceMappingURL=redact.js.map